import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Footer } from '@/components/Footer';
import { NavBar } from '@/components/NavBar';
import { useResponsive } from '@/hooks/useResponsive';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { getSystemInfo } from '@/utils/systemDetector';

import DownloadSection from './DownloadSection/DownloadSection';
import { useDownloadInfo } from './hooks/useDownloadInfo';
import styles from './index.module.less';

/**
 * Download 主页面组件
 * 展示 Kwaipilot 下载页面，包含不同操作系统的下载选项
 */
export const Download: React.FC = () => {
  const { t } = useTranslation();
  const { downloadInfo, loading } = useDownloadInfo();
  const [userSystem, setUserSystem] = useState<'apple' | 'intel' | 'windows' | null>(null);
  const [defaultMacType, setDefaultMacType] = useState<'arm' | 'intel'>('arm');
  const { isMobile } = useResponsive();

  useScrollToTop();

  // 页面初始化
  useEffect(() => {
    // 添加页面加载动画
    const pageElement = document.querySelector(`.${styles.downloadPage}`);
    if (pageElement) {
      const timer = setTimeout(() => {
        pageElement.classList.remove(styles.loading);
        pageElement.classList.add(styles.loaded);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  useEffect(() => {
    // 检测用户系统
    detectUserSystem();
  }, [userSystem]);

  // 检测用户系统
  const detectUserSystem = () => {
    const systemInfo = getSystemInfo();

    if (systemInfo.system === 'mac') {
      // 根据芯片类型设置默认选择
      if (systemInfo.chip === 'apple') {
        setDefaultMacType('arm');
        setUserSystem('apple');
      } else if (systemInfo.chip === 'intel') {
        setDefaultMacType('intel');
        setUserSystem('intel');
      } else {
        // 无法检测到芯片类型时，默认选择Apple Silicon
        setDefaultMacType('arm');
        setUserSystem('apple');
      }
    } else if (systemInfo.system === 'windows') {
      setUserSystem('windows');
    }
  };

  // 添加高亮类名到卡片
  useEffect(() => {
    if (!userSystem) return;

    const cards = document.querySelectorAll(`.${styles.downloadCard}`);
    cards.forEach((card, index) => {
      if (
        ((userSystem === 'apple' || userSystem === 'intel') && index === 0) || // macOS 卡片
        (userSystem === 'windows' && index === 1) // Windows 卡片
      ) {
        card.classList.add(styles.highlighted);
      } else {
        card.classList.remove(styles.highlighted);
      }
    });
  }, [userSystem, downloadInfo]);

  return (
    <div
      className={`${styles.downloadPage} ${styles.loading} ${isMobile ? styles.mobileLayout : ''}`}
    >
      {/* 导航栏 */}
      <NavBar />

      {/* 主内容区域 */}
      <div className={styles.mainContent}>
        <DownloadSection
          downloadInfo={downloadInfo}
          loading={loading}
          defaultMacType={defaultMacType}
          userSystem={userSystem}
        />
      </div>

      {/* 页脚 */}
      <Footer />
    </div>
  );
};

export default Download;
